import React, { useState, useRef } from 'react';
import { motion, useInView, AnimatePresence } from 'framer-motion';
import '../styles/FAQ.css';

const faqs = [
  {
    q: 'What makes CVEarity different from traditional vulnerability databases?',
    a: 'Most databases like NVD list every CVE without any context. CVEarity maps vulnerabilities directly to your stack, filters out the noise, and enriches what remains with exploit data and EPSS scores - so your team only sees what actually matters.',
  },
  {
    q: 'How does CVEarity track active exploitations?',
    a: 'We aggregate signals from NVD, MITRE, CISA KEV, and third-party threat feeds. When a CVE shifts from theoretical to actively exploited, we re-alert your team - even if it was previously deprioritized.',
  },
  {
    q: 'Is CVEarity suitable for small teams or only large enterprises?',
    a: 'CVEarity is built for both small teams and enterprises - it scales as your needs grow. Small teams get automation and simplicity, while enterprises get depth, control, and continuous monitoring.',
  },
  {
    q: 'Can I integrate CVEarity with Jira, Slack, or other tools?',
    a: 'Webhooks are available on all paid plans for custom integrations. Pre-built connectors for Slack, Microsoft Teams, Jira, and PagerDuty are on the active roadmap. Reach out if you need a specific integration prioritized.',
  },
  {
    q: 'Does CVEarity support compliance reporting?',
    a: 'Yes. Every CVE state change - new, acknowledged, patched, or accepted risk - is logged with timestamps and user attribution. These audit trails can be exported as PDF or JSON and are accepted by most SOC 2, ISO 27001, and PCI-DSS auditors.',
  },
];

function FAQItem({ q, a, i }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-40px' });

  return (
    <motion.div
      ref={ref}
      className={`faq-item ${open ? 'faq-item--open' : ''}`}
      initial={{ opacity: 0, y: 16 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.4, delay: i * 0.06 }}
    >
      <button className="faq-item__trigger" onClick={() => setOpen(!open)}>
        <span className="faq-item__q">{q}</span>
        <span className={`faq-item__icon ${open ? 'faq-item__icon--open' : ''}`}>
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <path d="M4 6L8 10L12 6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </span>
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            className="faq-item__body"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: [0.04, 0.62, 0.23, 0.98] }}
          >
            <p className="faq-item__a">{a}</p>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

export default function FAQ() {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-80px' });

  return (
    <section id="faq" className="faq section">
      <div className="container">
        <motion.div
          ref={ref}
          className="faq__head"
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.45 }}
        >
          <div className="section-eyebrow">FAQ</div>
          <h2 className="faq__h2">Frequently asked questions</h2>
          <p className="faq__sub">
            Everything you need to know before getting started with CVEarity.
          </p>
        </motion.div>

        <div className="faq__list">
          {faqs.map((item, i) => (
            <FAQItem key={i} {...item} i={i} />
          ))}
        </div>

        <motion.div
          className="faq__footer"
          initial={{ opacity: 0 }}
          animate={inView ? { opacity: 1 } : {}}
          transition={{ delay: 0.5 }}
        >
          <p className="faq__footer-text">Still have questions?</p>
          <a href="mailto:support@cvearity.com" className="btn-secondary">
            Contact us
          </a>
        </motion.div>
      </div>
    </section>
  );
}
